/* eslint-disable @typescript-eslint/ban-types */
import {
  Condition,
  TaxType,
  FinanceType,
  ExtendedFinanceType,
  CreditRating,
  Language,
  DateFormat,
  FuelEfficiencyUnit,
  CurrencyType,
  COUNTRIES,
  FixedValueFee,
  SpecialFees,
  FeeCategory,
  IncentivesProvider,
  CLIENT_TYPE,
  Origin,
  InsuranceEventType,
  InsuranceProvider,
  InventoryType,
  LeaseMileageNames,
  LeaseTermNames,
  PriceRuleSource,
  DealerCashRuleType,
  PriceRuleOffsetType,
  LendingFlowType,
  OnlineStep,
  WebPage,
  DealerType,
  ThirdPartyAccessoryProvider,
  IncomeIntervalType,
} from './constants';
import { DealerVerification } from './dealerVerificationTypes';
import { DealerGroup } from './dealerGroupTypes';
import { PENConfiguration, Product } from './penTypes';
import { Feed } from './feedTypes';
import { FeedView } from './feedViewTypes';
import {
  CreditPricingLeasePricing,
  CreditPricingLeaseTerm,
  CreditPricingPurchasePricing,
  CreditPricingPurchaseTerm,
  CreditTier,
  CreditPricing,
} from './creditPricingType';
import { LeadFormConfig } from './leadFormTypes';
import { MarketScanConfig, MarketScanTaxConfig } from './marketScanConfig';

export interface PriceRule {
  priceSource: PriceRuleSource;
  offsetType: PriceRuleOffsetType;
  offsetValue?: number;
  dealerCashRuleType?: DealerCashRuleType;
  dealerCashRuleValue?: number | null;
  dealerCashRuleValueByFinance?: {
    [index in FinanceType]?: number;
  };
  aprConfig?: AprConfig;
  creditPricing?: CreditPricing;
}

export interface CarPriceRule extends PriceRule {
  id: string;
  year?: number;
  make?: string;
  minYear?: number;
  maxYear?: number;
  model?: string;
  trim?: string;
  mfrModelCodes?: {
    code: string;
    name: string;
  }[];
  discount?: number;
  expiration?: string | null; // ISO 8601 timestamp
  vin?: string;
}

export interface PriceRulesEntry {
  defaultRule: PriceRule;
  carRules?: CarPriceRule[];
  enableDealerCashPriceOffset?: boolean;
  enableSelectInventoryRebate?: boolean;
}

// If the rule is enabled and any of maxMiles, maxYear or maxPrice are breached, the car will
// not be eligible for that type of ownership.
export type OwnershipRule = {
  enabled: boolean;
  maxMiles?: number;
  maxAge?: number;
  maxPrice?: number;
};

export type PriceRules = {
  [index in CLIENT_TYPE]?: {
    [index in Condition]?: PriceRulesEntry;
  };
};

export interface PurchaseTerms {
  term1: number;
  term2: number;
  term3: number;
}
export interface PurchaseTermsAPRMarkup {
  term1?: number;
  term2?: number;
  term3?: number;
}

type LeaseMileages = {
  [index in LeaseMileageNames]?: number;
};

export type CreditTiers = {
  [index in CreditRating]: {
    low?: number;
    high?: number;
  };
};

interface PurchaseInfo {
  terms?: PurchaseTerms;
  credit: {
    [index in CreditRating]: {
      apr?: PurchaseTerms;
      markup?: PurchaseTermsAPRMarkup;
    };
  };
}

interface LeaseInfo {
  mileages?: LeaseMileages;
  terms?: {
    [index in LeaseTermNames]?: {
      term?: number;
      credit?: {
        [index in CreditRating]?: {
          moneyFactor?: LeaseMileages;
          // Markup specifically for onePayLeases
          onePayLeaseMoneyFactor?: LeaseMileages;
        };
      };
    };
  };
}

export interface DealUpgrade {
  title: string;
  tags: string[];
  content: {
    headline: string;
    info: string[];
    cost: number;
    subtitle?: string;
    term?: string;
    deductible?: number;
  }[];
}

type ProtectionPlans = {
  [index in Condition]: {
    [index in FinanceType]?: DealUpgrade[];
  };
};

export interface AprConfig {
  enabled?: boolean;
  creditTiers?: CreditTiers;
  purchase?: PurchaseInfo;
  lease?: LeaseInfo;
  options?: {
    addOEMMarkupRate?: boolean;
    useManufacturerApr?: {
      purchase?: boolean;
      [CreditRating.EXCELLENT]?: boolean;
      [CreditRating.GOOD]?: boolean;
      [CreditRating.AVERAGE]?: boolean;
      [CreditRating.POOR]?: boolean;
    };
    useManufacturerRebate?: {
      purchase?: boolean;
      lease?: boolean;
      cash?: boolean;
    };
  };
}

export interface AprConfigWithCondition {
  [Condition.NEW]?: AprConfig;
  [Condition.USED]?: AprConfig;
  [Condition.CPO]?: AprConfig;
}

export interface AprConfigOptions {
  options?: {
    addOEMMarkupRate?: boolean;
    useManufacturerApr?: {
      purchase?: boolean;
      [CreditRating.EXCELLENT]?: boolean;
      [CreditRating.GOOD]?: boolean;
      [CreditRating.AVERAGE]?: boolean;
      [CreditRating.POOR]?: boolean;
    };
    useManufacturerRebate?: {
      purchase?: boolean;
      lease?: boolean;
      cash?: boolean;
    };
  };
}

export interface AprConfigWithOptions {
  [Condition.NEW]?: {
    options?: CreditPricing['options'];
  };
  [Condition.USED]?: {
    options?: CreditPricing['options'];
  };
  [Condition.CPO]?: {
    options?: CreditPricing['options'];
  };
}

export interface ValueDealerFee {
  type: typeof FixedValueFee;
  name: string;
  value: number;
  crmField?: string; // Which Add-On name to map to. Only for VINSolutions currently
  category?: string; // added by ATC or number-utils
  addToTotalAtStart?: boolean; // Check if fee should be added to total at start
  includeInFeeStack?: boolean; // Should this fee be included in fees stack?
  visualIndex?: number; // used for ordering fees
}

export interface AprDealerFee {
  type: SpecialFees;
  name: string;
  apr: number;
  crmField?: string; // Which Add-On name to map to. Only for VINSolutions currently
  category?: string; // added by ATC or number-utils
  addToTotalAtStart?: boolean; // Check if fee should be added to total at start
  includeInFeeStack?: boolean; // Should this fee be included in fees stack?
  value?: number; // computed value for use within offer display context, added by number-utils
  visualIndex?: number; // used for ordering fees
}

export type DealerFee = ValueDealerFee | AprDealerFee;

export function isFixedValueDealerFee(fee: DealerFee): fee is ValueDealerFee {
  return fee.type === FixedValueFee;
}

export type FeesByCategory = {
  [index in FeeCategory]?: DealerFee[];
};

export type DealerFees = {
  [Condition.NEW]: {
    [index in FinanceType | ExtendedFinanceType]?: FeesByCategory;
  };
  [Condition.USED]: {
    [index in FinanceType | ExtendedFinanceType]?: FeesByCategory;
  };
  [Condition.CPO]?: {
    [index in FinanceType | ExtendedFinanceType]?: FeesByCategory;
  };
};

// These add-ons are today only used for display and NOT for desking
// We might in the future want to copy these into desking but that is future work
// For now, we just have to live with the fact that dealer.addOns looks like pencil.addOns
// but has a different use case.
//
// @TODO: rethink addOns
export interface DealerAddOn {
  name: string;
  price: number;
  code?: string;
  // by default, add-ons are taxed for purchase and cash as they are included in the final selling price
  // we need this configuration only for lease
  taxableForLease?: boolean;
}

export interface VautoCredentials {
  username: string;
  password: string;
}

export interface FirstlookCredentials {
  dealerId: string;
  passcode: string;
  partnerId: string | number;
}

export type TradeInManagerCredentials = VautoCredentials | FirstlookCredentials;

export interface DealertrackCredentials {
  dealertrackId: string;
}

export interface RouteOneCredentials {
  routeOneId: string;
  routeOneUserId?: string;
}

export type FNICredentials = DealertrackCredentials | RouteOneCredentials;

export type DealerId = string;

export interface Manager {
  name?: string;
  email?: string;
  phone?: string;
  shouldEmailLeaderboard?: boolean;
  shouldSendTurnover?: boolean;
  shouldSendLead?: boolean;
  shouldSendPickup?: boolean;
  shouldSendDelivery?: boolean;
  shouldSendTestDrive?: boolean;
  shouldSendCreditApplication?: boolean;
  // Manager can receive completed signatures
  shouldReceiveSignatures?: boolean;
  // Manager can receive completed signatures
  shouldSendRemoteTestDrive?: boolean;
  // Only one New / Used vehicle manager can sign signatures
  isPrimaryNewSigner?: boolean;
  isPrimaryUsedSigner?: boolean;
}

export interface Day {
  enabled: boolean;
  start: number;
  end: number;
}

export interface Days {
  sunday: Day;
  monday: Day;
  tuesday: Day;
  wednesday: Day;
  thursday: Day;
  friday: Day;
  saturday: Day;
}

export interface TaxLevel {
  name: string;
  rate: number;
  useRate?: number; // defaults to rate, only for lease
  maxAmount?: number;
  amountOverride?: number; // overrides rate & upfront use rate (not monthly useRate) to set an amount
  // only filled in when describing the tax amount for a car in payments
  amount?: number;
  // Only used when applying tax based on customer's County
  customerCounty?: string;
  // Only used when applying tax based on customer's County
  customerState?: string;
  customerCity?: string;
}

export interface TaxBreakdown {
  [TaxType.STATE]?: TaxLevel;
  [TaxType.CITY]?: TaxLevel;
  [TaxType.COUNTY]?: TaxLevel;
  [TaxType.CUSTOMER_COUNTY]?: TaxLevel;
  [TaxType.INCEPTION]?: TaxLevel;
  taxByDealerAddress?: boolean;

  aggregatedUseTaxRate?: number; // only for lease and IL->Chicago tax and MS is enabled

  taxConfigurations?: MarketScanTaxConfig;
  warranty?: number;
}

export interface InsuranceConfig {
  online?: {
    enabled?: boolean;
    [OnlineStep.creditApplication]?: { enabled?: boolean };
    [OnlineStep.paymentBuilder]?: { enabled?: boolean };
    [WebPage.summary]?: { enabled?: boolean };
  };
  provider?: InsuranceProvider;
  insuranceEmail?: string;
}

// Used on the waiveFirstLeasePayment feature - this is the config for the dealer
export interface YearMakeModelConfig {
  year?: number;
  make?: string;
  model?: string;
  matchAnyYear?: boolean; // If this is true, it means any year can be matched
  matchAnyMakeModel?: boolean; // If this is true, it means any makeModel can be matched
}

export interface InceptionFeesConfig {
  taxes?: {
    enabled?: boolean;
  };
  fees?: {
    enabled?: boolean;
    // On/off status for separate fees
    feeStatus?: {
      [name: string]: boolean;
    };
  };
  firstMonth?: {
    enabled?: boolean;
  };
  customerToggle?: {
    enabled?: boolean;
  };
  enabledByDefault?: boolean;
}

export interface TaxInCapCostConfig {
  capCostReductionTax?: {
    enabled?: boolean;
  };
  paymentStreamTax?: {
    enabled?: boolean;
  };
}

export enum DisabledNotificationChannelType {
  emails = 'emails',
}

export enum DisabledNotificationType {
  testDrive = 'testDrive',
  testDriveIsWaiting = 'testDriveIsWaiting',
  pickupAndDelivery = 'pickupAndDelivery',
  newCarIsWaiting = 'newCarIsWaiting',
}

export type DisabledNotificationsType = {
  [channel in DisabledNotificationChannelType]?: {
    [notification in DisabledNotificationType]?: boolean;
  };
};

export enum ChatPreferenceType {
  all = 'all',
  chatOnly = 'chatOnly',
  disabled = 'disabled',
}

export interface Dealer {
  id: DealerId;
  name?: string | null;
  address?: string | null;
  city?: string | null;
  county?: string | null;
  state?: string | null; // province for Canadian dealers
  zip?: string | null; // postal code for Canadian dealers
  country?: COUNTRIES | null;
  timezone?: string | null;
  phone?: string | null;

  privacyPolicyUrl?: string | null;

  managers?: Manager[] | null;

  crmType?: string | null;
  crmEmail?: string | null;
  fniType?: string | null;
  tradeInManagerType?: string | null;
  firstlook?: FirstlookCredentials | null;
  vauto?: {
    enabled?: {
      [index in Origin.ONLINE | Origin.INSTORE]?: boolean;
    };
    entityId?: string;
    username?: string;
    login?: VautoCredentials;
  } | null;

  // dealersocket configurations
  dealersocket?: {
    dealersocketId: string;
    dealersocketSiteNumber: string;
  } | null;
  // reynolds configurations
  reynolds?: {
    defaultStatusId: string;
    demoCounterText?: string;
  } | null;
  // vinsolutions configurations
  vinsolutions?: {
    vinsolutionsId: string;
  } | null;
  // elead configurations
  elead?: {
    // Required for enabling appointment integration. (When creating a new appointment, this is the ID of the option in the dropdown)
    appointmentDropdownId?: string;
  } | null;
  // highergear configurations
  highergear?: {
    higherGearUrl: string;
  } | null;
  // common CRM configurations
  crmConfig?: {
    pushToDms: boolean;
    skateAlerts: boolean;
    restrictedDealAccess: boolean;
    adfXMLOnly: boolean;
    // searches lead for all dealer ids within dealer group. Applicable to Dealer Groups only.
    searchAllDealersInGroup?: boolean;
    // for VinSolutions only
    editSoldUndelivered?: boolean;
    useAppointmentsIntegration?: boolean;
  };
  // dealertrack configurations
  dealertrack?: {
    dealertrackId: string;
  } | null;
  // routeone configurations
  routeone?: {
    routeOneId: string;
    routeOneFallbackUserId?: string;
    enableSuppressR1Application?: boolean;
  } | null;
  // cudl configurations
  cudl?: {
    cudlId: string;
  } | null;
  // tekion (CRM) configurations
  tekion?: {
    dealerId: string;
  } | null;
  // Protection Plan Source type
  protectionPlanSource?: string | null;

  // pen configurations
  penConfig?: PENConfiguration;

  // pen products
  penProducts?: Product[];

  // verification configurations
  // these come from a different table
  dealerVerification?: DealerVerification | null;
  BYOConfig?: {
    crmBYOEmail?: string;
    isSDC?: boolean;
  };

  // wizard configurations
  wizard?: {
    pickup?: {
      enabled: boolean;
      startsAfter: string;
      endsAfter: string;
      days?: Days;
      appointmentWindow: string;
    };
    testDrive?: {
      enabled: boolean;
      startsAfter: string;
      endsAfter: string;
      days?: Days;
      appointmentWindow: string;
      remoteTestDriveEnabled: boolean;
      testDriveRadius: string;
    };
    delivery?: {
      enabled: boolean;
      startsAfter: string;
      endsAfter: string;
      radius: number;
      days?: Days;
    };
    splashPage?: {
      alternateHeaders?: boolean;
    };
    hotLeads?: {
      enabled?: boolean;
      emails?: string[];
    };
    priceValidation?: {
      enabled?: boolean;
      kbbApiKey?: string;
    };
    disclaimer?: {
      payments?: string;
    };
    paymentBuilder?: {
      showPaymentSummary?: boolean;
    };
    leadForm?: LeadFormConfig;
    loadCarsFromPartnerStores?: boolean;
    ctaClassic?: {
      enabled?: boolean;
    };
    enableProdigyWeb?: {
      enabled?: boolean;
      newWebsiteId?: string;
    };
    enableDocUpload?: {
      enabled?: boolean;
    };
    enableInstore?: boolean;
    instore?: {
      chatPreference: ChatPreferenceType;
    };
    enableCommandCenter?: boolean;
    routeDealToWebsiteDealer?: boolean;
  };
  // desking configurations
  desking?: {
    enabled: boolean;
    // enable default pencil
    defaultPencil: boolean;
    salespersonDesking?: boolean;
    showLeaseResidualValue?: boolean;
    managerDesking?: boolean;
    // configure a $10 price range for estimated monthly payment in papyrus & the pencil editor
    show10DollarMonthlyPriceRangeByDefault?: boolean; // shows this feature by default
    showPriceRangeWithoutAPROnSticker?: boolean;
    enable10DollarMonthlyPriceRangeToggle?: boolean; // enables a toggle for this feature in the pencil editor
    // use atc for taxes and fees
    useATCTaxes?: boolean;
    useATCFees?: boolean;
    addATCFeesToCapCost?: boolean;
    useTradeInAllowanceForTax?: {
      enabled: boolean;
      [Condition.NEW]: boolean;
      [Condition.USED]: boolean;
      [Condition.CPO]: boolean;
    };
    useCapitalizedTax?: boolean;
    capitalizedTaxRate?: number; // percentage
    hideNetTradeIn?: boolean;
    showBiweeklyPayments?: boolean;
    showAdditionalCustomerIncentives?: boolean;
    showAllAdditionalCustomerIncentives?: boolean;
    showSeparateLowInterestHighRebateOffers?: {
      enabled?: boolean;
      defaultOffer?: string;
    };
    defaultPack?: number;
    monthlyPriceRangeValue?: number;
    addInceptionFeesToTotalAtStart?: boolean;
    includeTaxInCapCost?: boolean;
    ignoreMRM?: boolean;
    enableOnePayLease?: boolean;
    differentOnePayFeesFromLease?: boolean;
    applyRebatesToDueAtSigning?: boolean;
    inceptionFeesTotalAtStart?: InceptionFeesConfig;
    taxInCapCost?: TaxInCapCostConfig;
    onlineDesking?: boolean;
    enableLeaseApr?: boolean;
    useDeskingCRMPush?: boolean;
    filterStandardWhenSpecialExists?: boolean;
    fallbackToStandardLeaseRates?: boolean;
  };
  // tablet configurations
  tabletFormsConfig?: {
    // configuration for customer info page
    customerInfo?: {
      email: boolean;
      phone: boolean;
      showInstoreOnlineLink: boolean;
    };
    // vehicle selection page configuration
    vehicleSelection?: {
      // show monthly payment on car cards
      monthlyPayment: boolean;
      daysUntilAgedUnit?: {
        [index in Condition]: number | null;
      };
    };
    // enable five line
    fiveLine?: {
      enabled: boolean;
    };
    // enables a prompt that allows sales people to mark cars as sale pending
    enableSalePendingPrompt?: boolean;
    // boolean flag to disable customer acceptance signature in Papyrus
    disableCustomerSignature?: boolean;
    driverLicense?: {
      coloredTrueSize?: boolean;
    };
  };

  fastringUrl?: string | null;
  crmDefaults?: { [index: string]: unknown } | null;
  defaultApr?: number | null;
  canImportInventory?: boolean;

  valueGuarantee?: object | null;
  logo?: string | null;
  stickerLogo?: string | null;
  fees?: DealerFees | null;

  inventory?: object[] | null;
  controlPanelFlow?: object[] | null;
  stepFlowConfig?: object[] | null;
  printingType?: string | null;
  testDriveSpeed?: number | null;
  testDriveDuration?: number | null;
  isIndependent?: boolean;
  brand?: string[] | null;
  taxes?: { [index in FinanceType]?: TaxBreakdown } | null;
  addOns?: { [index in Condition]: DealerAddOn[] } | null;
  priceRules?: PriceRules | null;
  protectionPlans?: ProtectionPlans | null;
  aprConfig?: AprConfigWithCondition | null;
  inStoreAprConfig?: AprConfigWithCondition | null;
  aprConfigOptionsNew?: AprConfigWithOptions | null;
  inStoreAprConfigOptionsNew?: AprConfigWithOptions | null;
  supportEmail?: string | null;
  financeEmail?: string | null;
  wizardNotary?: string | null;
  wizardEmail?: string | null;

  // miscellaneous feature configurations
  featureConfig?: FeatureConfig | null;

  localizationConfig?: {
    defaultLanguage?: Language;
    supportedLanguages?: Language[];
    dateFormat?: DateFormat;
    fuelEfficiencyUnit?: FuelEfficiencyUnit;
    currency?: CurrencyType;
  } | null;
  // Feed configurations - these come from a different table
  Feeds?: Feed[];
  DealerGroup?: DealerGroup;

  soldLeadsSyncedAt?: Date | string;
  createdAt?: string;
  updatedAt?: string;
  // Set this when dealer finally goes live on production
  liveAt?: Date | string;
  // Set this when dealer cancelled
  cancelledAt?: Date | string;

  // For a given configuration,
  ownershipOptionsRules?: {
    disabledDefaultPayment?: boolean;
    [Condition.NEW]: {
      [CLIENT_TYPE.ONLINE]: {
        [FinanceType.CASH]: OwnershipRule;
        [FinanceType.PURCHASE]: OwnershipRule;
        [FinanceType.LEASE]: OwnershipRule;
      };
      [CLIENT_TYPE.IN_STORE]: {
        [FinanceType.CASH]: OwnershipRule;
        [FinanceType.PURCHASE]: OwnershipRule;
        [FinanceType.LEASE]: OwnershipRule;
      };
    };
    [Condition.CPO]: {
      [CLIENT_TYPE.ONLINE]: {
        [FinanceType.CASH]: OwnershipRule;
        [FinanceType.PURCHASE]: OwnershipRule;
        [FinanceType.LEASE]?: never;
      };
      [CLIENT_TYPE.IN_STORE]: {
        [FinanceType.CASH]: OwnershipRule;
        [FinanceType.PURCHASE]: OwnershipRule;
        [FinanceType.LEASE]?: never;
      };
    };
    [Condition.USED]: {
      [CLIENT_TYPE.ONLINE]: {
        [FinanceType.CASH]: OwnershipRule;
        [FinanceType.PURCHASE]: OwnershipRule;
        [FinanceType.LEASE]?: never;
      };
      [CLIENT_TYPE.IN_STORE]: {
        [FinanceType.CASH]: OwnershipRule;
        [FinanceType.PURCHASE]: OwnershipRule;
        [FinanceType.LEASE]?: never;
      };
    };
  };

  onlineFeedView?: FeedView | null;
  inStoreFeedView?: FeedView | null;
  crmEmailNew?: string;
  crmEmailUsed?: string;
  insurance?: InsuranceConfig;
  isDealerGroupConfigHolder?: boolean;
  launchpad?: {
    // this is a partial type definition
    creditPricing?: {
      [FinanceType.PURCHASE]?: {
        creditTiers?: CreditTier[];
        pricing?: CreditPricingPurchasePricing[][];
        terms?: CreditPricingPurchaseTerm[];
      };
      [FinanceType.LEASE]?: {
        creditTiers?: CreditTier[];
        pricing?: CreditPricingLeasePricing[][];
        terms?: CreditPricingLeaseTerm[];
      };
    };
    email?: string | null;
    rocketLauncherEmail?: string | null;
    inventory?: {
      provider?: InventoryType;
    };
    status?: {
      finishedWidgetConfig?: boolean;
      readyToLaunch?: boolean;
      readyToBeginInventoryConfig: boolean;
    };
    otherCrmName?: string; // deprecated REL-6569
    firstLogin?: string;
    features?: {
      online?: boolean;
      instore?: boolean;
    };
  } | null;
  lending?: LendingConfig;
  waiveFirstLeasePaymentYearMakeModels?: YearMakeModelConfig[];
  disabledNotifications?: DisabledNotificationsType;
  creditFormConfig?: CreditFormConfig;
  dealerType?: DealerType;

  // dms related fields
  dmsType?: string | null;
  // CDK
  dmsCDK?: {
    dealerId?: string;
    closingManagerId?: string;
    fiManagerId?: string;
    salesMangerId?: string;
    salesPersonIds?: string[];
  };
  dmsConfig?: {
    // CDK specific fields
    cdk?: {
      dealerId?: string;
    };
    dealertrack?: {
      companyNumber?: string; // DT specific company number
      enterpriseCode?: string; // DT specific enterprise code
      serverName?: string; // allow overriding the server name per dealer
    };
    tekion?: {
      dealerId?: string;
    };
    closingManagerId?: string; // these are potentially common across both
    fiManagerId?: string; // these are potentially common across both
    salesManagerId?: string; // these are potentially common across both
    salesPersonIds?: string[]; // these are potentially common across both
  };
  otherCrmName?: string;
  otherDmsName?: string;
  dealergroup_id?: string | null;
  dealerWebsiteUrl?: string | null;
  displayConfig?: {
    hideUsedCarListPrice?: boolean;
    showOnlineMSRP?: boolean;
    showInstoreMSRP?: boolean;
    differentPriceSource?: {
      useDifferentPriceSourceForUnregistered?: boolean;
      priceRuleSourceSelector?:
        | PriceRuleSource.MSRP
        | PriceRuleSource.INVOICE
        | PriceRuleSource.FEED_PRICE;
      availableCarsYears?: {
        nextYear?: boolean;
        currentYear?: boolean;
        lastYear?: boolean;
        yearBeforeLast?: boolean;
      };
    };
  } | null;
  rooftopSlug?: string;
  marketScanConfig?: MarketScanConfig;
  translationRules?: {
    instore?: {
      enabled: boolean;
      rulesId: string;
    };
  } | null;
}

export interface DealerWithFlags extends Dealer {
  // Not in db schema because it is injected by getFlagsForDealer
  flags: { [index: string]: string | boolean | undefined };
}

export interface DealerFeatureConfigDeposits {
  amount?: number;
  cpoCars?: boolean;
  enabled?: boolean;
  newCars?: boolean;
  stripePrivateKey?: string;
  stripePublicKey?: string;
  stripeWebhookEnabled?: boolean;
  stripeWebhookId?: string;
  stripeWebhookSecret?: string;
  usedCars?: boolean;
}

export interface FeatureConfig {
  // ftp upload printed pdfs to a client server
  ftpUpload?: {
    enabled: boolean;
    hostname: string;
    username: string;
    password: string;
    path?: string;
  };

  // Informativ DSGSS configurations
  informativ?: {
    enabled: boolean;
    dealershipId?: string;
  };

  showDealerBrandsFirst?: boolean;
  // soft credit pull for end customers
  softCreditPull?: {
    online: boolean;
    inStore: boolean;
    digitalFinance: boolean;
    inStoreCreditApp: boolean;
    username: string;
    password: string;
  };
  // shift digital configurations
  shiftDigital?: {
    enabled: boolean;
    retailerID: string;
    dealerID: string;
    apiToken: string;
    useShiftOffers: boolean;
    importShiftAccessories?: boolean;
    importShiftPricing?: boolean;
    taggingEnabled?: boolean;
  };
  // Urban Science configuration
  urbanScience?: {
    enabled?: boolean;
    dealerCode?: string;
  };
  unite?: {
    enabled?: boolean;
    dealerCode?: string;
    taggingEnabled?: boolean;
  };
  thirdPartyLeads?: {
    forwardEmailToCustomer: boolean;
    forwardTextToCustomer: boolean;
    forwardTextDelayMinutes?: number;
    forwardEmailDelayMinutes?: number;
  };
  // global pricing configurations
  globalPricing?: {
    usePriceOverrides?: { [index in Origin]?: boolean };
    forwardedPriceOverrideExpiration?: number; // in # of days
  };
  crmDirectLogin?: {
    enabled: boolean;
  };
  virtualShowroom?: {
    enabled: boolean;
    displayMultipleChatRequests?: boolean;
  };
  // supported finances types for this dealer
  financeTypes?: FinanceType[];
  incentives?: {
    // incentives provider to use
    provider?: IncentivesProvider;

    // Only applies to ChromeData
    useOnlySubventedOffers?: boolean;

    // Only applies to AIS
    excludeIncentivesFromAffiliates?: boolean;
  };
  inventoryImages?: {
    chromeDataIsPrimaryImageSource?: boolean;
    shiftDigitalInventorySolutionIsPrimaryImageSource?: boolean;
  };
  multiDealerManagement?: {
    loadCarsFromPartnerStoresInStore?: boolean;
    enableCrossSellingFromPartnerStores?: boolean;
  };
  subaruGtp?: {
    enabled: boolean;
    showKBBOption?: boolean;
  };
  acuTrade?: {
    enabled: boolean;
    dealerID?: string;
    dealerKey?: string;
    showKBBOption?: boolean;
  };
  kbbIco?: {
    enabled: boolean;
    enabledInStore: boolean;
    dealerId?: string;
    showKBBOption?: boolean;
    groupIdRequired?: boolean;
    groupId?: string;
  };
  blackBook?: {
    enabled: boolean;
  };
  forwardInstabugReports?: {
    enabled: boolean;
    emails?: string[];
  };
  formsConfig?: {
    mandatoryEmployersAddress?: boolean;
  };
  displayPricesWithIncentives?: {
    adjustOnline?: boolean;
  };
  downPayment?: {
    default?: number;
    downPaymentOptions?: {
      isPercentage?: boolean;
      fixedDownPaymentA?: number;
      fixedDownPaymentB?: number;
      fixedDownPaymentC?: number;
      percentageDownPaymentA?: number;
      percentageDownPaymentB?: number;
      percentageDownPaymentC?: number;
    };
    disableEditBySalesPerson?: boolean;
  };
  downPaymentTradeValueLimit?: {
    [FinanceType.PURCHASE]?: {
      enabled?: boolean;
      percentage?: number;
    };
    [FinanceType.LEASE]?: {
      enabled?: boolean;
      percentage?: number;
    };
  };
  eContracting?: {
    enabled: boolean;
  };
  importOEMOptionalAccessories?: boolean;
  daysToFirstPayment?: number;
  customFeedPrice?: {
    enabled?: boolean;
  };
  displaySavings?: {
    enabled?: boolean;
  };
  useCPO?: {
    enabled?: boolean;
  };
  kiaReservation?: {
    enabled?: boolean;
  };
  kbbDefaultOption?: {
    enabled?: boolean;
  };
  tradeEquity?: {
    enabled: boolean;
  };
  tradePending?: {
    enabled?: boolean;
    showInStore?: boolean;
    showOnline?: boolean;
  };
  autodata?: {
    enabled?: boolean;
    dealerId?: string;
  };
  insigniaAccessoryConfig?: {
    enabled?: boolean;
  };
  thirdPartyAccessoryProvider?: ThirdPartyAccessoryProvider | null;
  inventoryDefaultSort?: string;
  showOnLotInventoryFirst?: boolean;
  inventoryConditionDisplay?: {
    displayCheckboxes?: boolean;
  };
  disableTaxes?: boolean;
  deposits?: DealerFeatureConfigDeposits;
  audiInTransit?: {
    enabled: boolean;
    dealerCode?: string;
  };
  taxByDealerAddress?: boolean;

  useWarrantyTaxForLease?: boolean;
  useWarrantyTaxForPurchaseAndCash?: boolean;

  newLendingTiersMatch?: {
    purchase?: boolean;
    lease?: boolean;
  };
}

export interface LendingConfig {
  instore?: {
    enabled?: boolean;
    aprMarkup?: number;
    submitDirectlyFromCreditApplication?: boolean; // deprecated. REL-6689
    flowType?: LendingFlowType;
    manualFISubmission?: boolean;
    submitToSelectedLenders?: boolean;
    enableAcceptOfferScreen?: boolean;
  };
  online?: {
    enabled?: boolean;
    aprMarkup?: number;
    submitDirectlyFromCreditApplication?: boolean; // deprecated. REL-6689
  };
  web?: {
    enabled?: boolean;
  };
  westlake?: {
    enabled?: boolean;
    dealerId?: string;
    creditTiers?: {
      low?: number;
      high?: number;
    } & { [index in CreditRating]?: boolean };
  };
  upstart?: {
    enabled?: boolean;
    creditTiers?: {
      low?: number;
      high?: number;
    } & { [index in CreditRating]?: boolean };
    upstartCreditFirst?: boolean;
    standaloneLending?: {
      enabled?: boolean;
    };
  };
  submittedLendingInfo?: boolean;
  businessHours?: Days;
  digitalFinance?: {
    enabled?: boolean;
    manualFISubmission?: boolean;
    submitToSelectedLenders?: boolean;
  };
  accountNickname?: string;
  allowOptionalInvoiceOrBookValue?: boolean;
  creditWidget?: {
    instore?: {
      enabled?: boolean;
    };
    online?: {
      enabled?: boolean;
    };
  };
}

// price rule history types
export type PriceRuleDiff = {
  [index in keyof PriceRule]?: {
    old: PriceRule[index] | undefined;
    new: PriceRule[index] | undefined;
  };
} & {
  dealerCashEnabled?: {
    old: boolean | undefined;
    new: boolean | undefined;
  };
  selectInventoryRebateEnabled?: {
    old: boolean | undefined;
    new: boolean | undefined;
  };
};

export type CarPriceRuleAddRemoveDiff = {
  id: string;
  type: 'add' | 'remove';
  rule: CarPriceRule;
};

export type CarPriceRuleChangeDiff = {
  id: string;
  type: 'change';
  diff: {
    [index in keyof CarPriceRule]?: {
      old: CarPriceRule[index] | undefined;
      new: CarPriceRule[index] | undefined;
    };
  };
};

export type CarPriceRuleDiff =
  | CarPriceRuleAddRemoveDiff
  | CarPriceRuleChangeDiff;

export interface PriceRulesEntryDiff {
  defaultRuleDiff?: PriceRuleDiff;
  carRulesDiff?: CarPriceRuleDiff[];
}

export type PriceRulesRevision = {
  id: string;
  userId: number;
  dealerId: string;
  diff: {
    [index in CLIENT_TYPE]?: {
      [index in Condition]?: PriceRulesEntryDiff;
    };
  };
  createdAt: string;
};

export interface InsuranceEventCounts {
  dealerName: string;
  providerName: string;
  applications: {
    [Origin.ONLINE]: number;
    [Origin.INSTORE]: number;
  };
  texts: {
    [InsuranceEventType.TEXT_CLICK]: number; // must be counted once per deal
    [InsuranceEventType.TEXT_CONVERSION]: number;
    [InsuranceEventType.TEXT_SENT]: number;
  };
}

export interface InsuranceApplicationTotals {
  origins: {
    [Origin.ONLINE]: number;
    [Origin.INSTORE]: number;
  };
  providers: {
    [index in InsuranceProvider]: number;
  };
  text: number;
}

export interface ApplicantAttrType {
  mandatory?: boolean;
  dependencies?: string[];
}

export interface ApplicantReference {
  firstName?: ApplicantAttrType;
  lastName?: ApplicantAttrType;
  phone?: ApplicantAttrType;
  address?: ApplicantAttrType;
  city?: ApplicantAttrType;
  state?: ApplicantAttrType;
  zip?: ApplicantAttrType;
}

export interface Applicant {
  firstName?: ApplicantAttrType;
  middleName?: ApplicantAttrType;
  lastName?: ApplicantAttrType;
  phone?: ApplicantAttrType;
  homePhone?: ApplicantAttrType;
  email?: ApplicantAttrType;
  driverLicenseNumber?: ApplicantAttrType;
  driverLicenseState?: ApplicantAttrType;
  birthday?: ApplicantAttrType;
  socialSec?: ApplicantAttrType;
  address?: ApplicantAttrType;
  city?: ApplicantAttrType;
  state?: ApplicantAttrType;
  zip?: ApplicantAttrType;
  county?: ApplicantAttrType;
  apt?: ApplicantAttrType;
  yearsAtAddress?: ApplicantAttrType;
  monthsAtAddress?: ApplicantAttrType;
  rentType?: ApplicantAttrType;
  mortgageLease?: ApplicantAttrType;
  prevAddress?: ApplicantAttrType;
  prevCity?: ApplicantAttrType;
  prevState?: ApplicantAttrType;
  prevZip?: ApplicantAttrType;
  prevApt?: ApplicantAttrType;
  prevYearsAtAddress?: ApplicantAttrType;
  prevMonthsAtAddress?: ApplicantAttrType;
  employmentStatus?: ApplicantAttrType;
  jobTitle?: ApplicantAttrType;
  companyName?: ApplicantAttrType;
  companyAddress?: ApplicantAttrType;
  companyState?: ApplicantAttrType;
  companyCity?: ApplicantAttrType;
  companyZip?: ApplicantAttrType;
  yearsAtCompany?: ApplicantAttrType;
  monthsAtCompany?: ApplicantAttrType;
  employerPhone?: ApplicantAttrType;
  monthlySalary?: ApplicantAttrType;
  otherIncome?: ApplicantAttrType;
  otherIncomeSource?: ApplicantAttrType;
  prevJobTitle?: ApplicantAttrType;
  prevCompanyName?: ApplicantAttrType;
  prevCompanyPhone?: ApplicantAttrType;
  prevYearsAtCompany?: ApplicantAttrType;
  prevMonthsAtCompany?: ApplicantAttrType;
  references?: ApplicantReference[];
  signature?: ApplicantAttrType;
}

export interface CreditFormConfig {
  applicant?: Applicant;
  coapplicant?: Applicant & { coapplicantRelation?: ApplicantAttrType };
  financeType?: ApplicantAttrType;
  incomeIntervalType?: IncomeIntervalType;
  references?: ApplicantAttrType;
}
